/**
 * printer-file-type — the print station's printer file-type rules (BLU-1336).
 *
 * Pure, no React. The page derives everything it shows about "this printer's
 * file" from here, so the stop, the disabled buttons, the pill and the
 * print-time wording can never disagree.
 *
 * The three rules:
 *
 *  1. STOP ONLY ON PROOF. A side is stopped only when THIS printer's lane is
 *     `absent` AND the other printer's lane is `present`. An `absent` lane
 *     exists only from the scan's own live storage probe (a stored manifest
 *     only ever reports `present` or `unknown`), so a stale record can never
 *     stop a print. Anything unknown falls back to today's behaviour: a false
 *     stop is worse than a missed one.
 *  2. ONLY A KORA IS TOLD TO USE THE OTHER PRINTER. At HQ the .arx4 is made
 *     from the PNG later (RIP lag) and a floor may have no Kora at all, so a
 *     Brother missing its .arx4 is told its file is not there yet, never sent
 *     to a Kora.
 *  3. NEVER SAY "NOT UPLOADED" WITHOUT PROOF. The print-time wording never
 *     claims a file is missing unless the other printer's lane is proven
 *     absent; otherwise it names the file this printer needs.
 *
 * The station's printer identity is display and guard evidence only. It never
 * feeds hasFront / hasBack / isDualPrint or any backend coverage decision.
 */
import type { PrintErrorCode, PrintFileSideLanes, PrinterFormat } from '@threadheads/shared';

/** A printable side of a garment. */
export type PrintSide = 'front' | 'back';

/** Printer name, file type label, extension and the other format, per format. */
export const PRINTER_FILE_TYPE = {
  png: { printer: 'Kora', type: 'PNG', ext: '.png', other: 'arx4' },
  arx4: { printer: 'Brother', type: 'ARX4', ext: '.arx4', other: 'png' },
} as const satisfies Record<
  PrinterFormat,
  { printer: string; type: string; ext: string; other: PrinterFormat }
>;

/**
 * Which operator copy to show. `format` is the format whose printer and
 * extension the copy names:
 *  - `wrong_printer`: "Use a {printer} Printer" (only ever a Kora told to use
 *    a Brother).
 *  - `no_file_for_printer`: "No {ext} File For This Printer".
 */
export interface PrinterFileCopy {
  kind: 'wrong_printer' | 'no_file_for_printer';
  format: PrinterFormat;
}

/** The parts of a failed `/print` result the print-time rule reads. */
export interface PrintFailureFacts {
  code?: PrintErrorCode;
  stage?: string;
  httpStatus?: number;
  detailCode?: string;
}

/**
 * Rule 1. True only when this printer's lane is proven absent AND the other
 * printer's lane is proven present. No declared format or no lanes: false.
 */
export function isSideBlocked(
  need: PrinterFormat | null,
  sideLanes: PrintFileSideLanes | null,
): boolean {
  if (need === null || sideLanes === null) return false;
  const other = PRINTER_FILE_TYPE[need].other;
  return sideLanes[need] === 'absent' && sideLanes[other] === 'present';
}

/** Rule 2. The copy for a stopped side: a Kora is pointed at the Brother; a
 *  Brother is only told its .arx4 is not there. */
export function blockCopy(need: PrinterFormat): PrinterFileCopy {
  return need === 'png'
    ? { kind: 'wrong_printer', format: 'arx4' }
    : { kind: 'no_file_for_printer', format: 'arx4' };
}

/**
 * Rule 3. Print-time wording for a failed print, or null to keep today's copy.
 * Never yields `wrong_printer` for a Brother (`need === 'arx4'`).
 */
export function printFailureCopy(
  need: PrinterFormat | null,
  sideLanes: PrintFileSideLanes | null,
  failure: PrintFailureFacts,
): PrinterFileCopy | null {
  if (need === null) return null;
  const own = sideLanes?.[need] ?? 'unknown';
  const other = sideLanes?.[PRINTER_FILE_TYPE[need].other] ?? 'unknown';
  // Only a Kora with the .arx4 proven and its own png not proven is pointed
  // at a Brother.
  const koraUseBrother = need === 'png' && other === 'present' && own !== 'present';

  switch (failure.code) {
    case 'custom_artwork_absent':
      // The other printer's file is proven absent too: today's "missing" copy
      // is true, keep it.
      if (other === 'absent') return null;
      if (koraUseBrother) return { kind: 'wrong_printer', format: 'arx4' };
      return { kind: 'no_file_for_printer', format: need };
    case 'artwork_file_absent':
      // Only the agent's own drop-folder refusal has this shape (a storage
      // download failure always carries an HTTP status).
      if (failure.stage !== 'download' || failure.httpStatus !== undefined) return null;
      if (need === 'arx4') return { kind: 'no_file_for_printer', format: 'arx4' };
      return koraUseBrother ? { kind: 'wrong_printer', format: 'arx4' } : null;
    case 'print_open_failed':
      // A Brother driver handed a file it cannot take. Only when the .arx4 is
      // not proven present does that mean "no .arx4 yet"; with the .arx4
      // present the driver itself cannot take it (a GT-3 style driver), so
      // today's generic copy stays.
      if (need === 'arx4' && failure.detailCode === 'unsupported_extension' && own !== 'present') {
        return { kind: 'no_file_for_printer', format: 'arx4' };
      }
      return null;
    case 'print_file_not_available':
      // That code also means an unresolved SKU or no catalogue row, so a Kora
      // only gets the neutral copy, and only when its png is not proven.
      if (need === 'png' && own !== 'present') return { kind: 'no_file_for_printer', format: 'png' };
      return null;
    default:
      return null;
  }
}

/**
 * The i18n keys and values for one printer file-type message. Every key is a
 * literal here so `check:i18n` sees it; the page only calls `t()`.
 * `sideKey` is the side label to translate into `params.side`, set only when
 * the body names one side.
 */
export interface FileTypeMessage {
  titleKey: 'printer.fileType.useOtherTitle' | 'printer.fileType.noFileTitle';
  bodyKey:
    | 'printer.fileType.useOtherBody'
    | 'printer.fileType.useOtherSideBody'
    | 'printer.fileType.noFileBody'
    | 'printer.fileType.noFileSideBody'
    | 'printer.fileType.notYetBody'
    | 'printer.fileType.notYetSideBody';
  params: { printer?: string; ext: string };
  sideKey: 'filmPrint.sideFront' | 'filmPrint.sideBack' | null;
}

/**
 * Which message to show for `copy` on `sides` of a garment with
 * `applicableCount` printable sides. A message covering every side names no
 * side.
 *
 * A Brother missing its .arx4 is the RIP-lag case: the file is usually made
 * a little later, so it is told to Retry Scan in a moment (mirroring the
 * not-ready banner), never to report an issue. A Kora missing its png keeps
 * the Report Print Issue copy.
 */
export function fileTypeMessage(
  copy: PrinterFileCopy,
  sides: readonly PrintSide[],
  applicableCount: number,
): FileTypeMessage {
  const { printer, ext } = PRINTER_FILE_TYPE[copy.format];
  const everySide = sides.length >= applicableCount;
  const sideKey = everySide ? null : sides[0] === 'back' ? 'filmPrint.sideBack' : 'filmPrint.sideFront';
  if (copy.kind === 'wrong_printer') {
    return {
      titleKey: 'printer.fileType.useOtherTitle',
      bodyKey: everySide ? 'printer.fileType.useOtherBody' : 'printer.fileType.useOtherSideBody',
      params: { printer, ext },
      sideKey,
    };
  }
  if (copy.format === 'arx4') {
    return {
      titleKey: 'printer.fileType.noFileTitle',
      bodyKey: everySide ? 'printer.fileType.notYetBody' : 'printer.fileType.notYetSideBody',
      params: { ext },
      sideKey,
    };
  }
  return {
    titleKey: 'printer.fileType.noFileTitle',
    bodyKey: everySide ? 'printer.fileType.noFileBody' : 'printer.fileType.noFileSideBody',
    params: { ext },
    sideKey,
  };
}
